import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  useReducer,
  useEffectEvent,
} from 'react';
import { useSmartFormat } from './knowledge-content-editor/hooks/use-smart-format';
import { useNavigate } from 'react-router-dom';
import { KNOWLEDGE_TYPES, ROUTES } from 'shared/constants';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { Button } from '../design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHourglass, faXmark } from '@fortawesome/pro-solid-svg-icons';
import styles from './knowledge-content.module.scss';
import { notification } from 'antd';
import { ConfirmationPopupComponent } from '../common/confirmation-popup/confirmation-popup.component';

const ConversionContext = createContext();

const createNotification = (
  api,
  type,
  { message, description, key, btn, icon, duration = 0, closeIcon = false, onClose },
) => {
  api[type]({
    message,
    description,
    key,
    btn,
    duration,
    closeIcon,
    icon,
    onClose,
  });
};

const CONVERSION_ACTIONS = Object.freeze({
  UPDATE: 'UPDATE',
  REMOVE: 'REMOVE',
  CLEAR: 'CLEAR',
});

const conversionsReducer = (state, action) => {
  switch (action.type) {
    case CONVERSION_ACTIONS.UPDATE:
      return {
        ...state,
        [action.assetId]: { ...state[action.assetId], ...action.update },
      };
    case CONVERSION_ACTIONS.REMOVE: {
      const { [action.assetId]: _, ...rest } = state;
      return rest;
    }
    case CONVERSION_ACTIONS.CLEAR:
      return {};
    default:
      return state;
  }
};

export const ConversionProvider = ({ children }) => {
  const [notificationApi, contextHolder] = notification.useNotification({
    placement: 'bottomLeft',
    stack: { threshold: 3 },
  });
  const lang = useSelector(getLang('BACKGROUND_CONVERSION'));
  const [conversions, dispatch] = useReducer(conversionsReducer, {});
  const { convertToSmartFormat } = useSmartFormat();
  const navigate = useNavigate();
  const isActive = useRef(true);
  const abortControllers = useRef({});
  const [editorDirtyState, setEditorDirtyState] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const getConversionStatus = assetId => conversions[assetId]?.status;
  const getConversionResult = assetId => conversions[assetId]?.results;
  const isConvertingKnowledge = knowledgeId =>
    Object.values(conversions).some(conv => conv.knowledgeId === knowledgeId);

  const getActiveConversions = () => {
    return Object.values(conversions).filter(
      conv => conv.status === 'IN_PROGRESS' || conv.status === 'DONE',
    ).length;
  };

  const closeToast = React.useCallback(
    key => {
      setTimeout(() => {
        notificationApi.destroy(key);
      }, 0);
    },
    [notificationApi],
  );

  const clearAssetToasts = React.useCallback(
    assetId => {
      // Stop the polling loop for this asset
      abortControllers.current[assetId]?.abort();
      delete abortControllers.current[assetId];

      const toastKeys = [
        `conversion-started-${assetId}`,
        `conversion-completed-${assetId}`,
        `conversion-failed-${assetId}`,
      ];
      toastKeys.forEach(key => closeToast(key));
      dispatch({ type: CONVERSION_ACTIONS.REMOVE, assetId });
    },
    [closeToast],
  );

  // Abort all in-progress polling on unmount
  useEffect(() => {
    return () => {
      Object.values(abortControllers.current).forEach(controller => controller.abort());
    };
  }, []);

  const clearConversions = useCallback(() => {
    // prevent future notifications
    isActive.current = false;
    // abort all in-progress polling
    Object.values(abortControllers.current).forEach(controller => controller.abort());
    abortControllers.current = {};
    // clear all notifications — read keys before clearing state
    Object.keys(conversions).forEach(assetId => {
      closeToast(`conversion-started-${assetId}`);
      closeToast(`conversion-completed-${assetId}`);
      closeToast(`conversion-failed-${assetId}`);
    });
    dispatch({ type: CONVERSION_ACTIONS.CLEAR });
  }, [conversions, closeToast]);

  const executeNavigation = useCallback(
    ({ targetPath, initialData, assetId, replace = false }) => {
      clearAssetToasts(assetId);
      navigate(targetPath, { state: { initialData }, replace });
    },
    [clearAssetToasts, navigate],
  );

  const handleConfirmNavigation = useCallback(() => {
    if (pendingNavigation) {
      // Replace the current (discarded) editor page in history so back button
      // skips it and goes to the expected previous page.
      executeNavigation({ ...pendingNavigation, replace: true });
      setPendingNavigation(null);
    }
  }, [pendingNavigation, executeNavigation]);

  const handleCancelNavigation = useCallback(() => {
    setPendingNavigation(null);
  }, []);

  // useEffectEvent ensures this always reads the latest editorDirtyState and executeNavigation,
  // even when called from stale notification button closures.
  const navigateToItem = useEffectEvent(
    ({ assetId, knowledgeId, result, fileName, categoryId, asset, options }) => {
      const targetPath = knowledgeId
        ? ROUTES.KNOWLEDGE_ITEMS_EDIT.replace(':id', knowledgeId)
        : ROUTES.KNOWLEDGE_ITEMS_NEW;

      const initialData = {
        assetId: asset.assetId,
        content: result.data,
        type: KNOWLEDGE_TYPES.CONTENT,
        link: options.link || asset.url,
        fileName,
        categoryId,
        title: options.knowledgeTitle,
        subtitle: options.subtitle,
        tags: options.tags,
        labels: options.labels,
        ownerIds: options.ownerIds,
      };

      const navigationPayload = { targetPath, initialData, assetId };

      const currentPath = window.location.pathname;
      const isOnEditorPage = currentPath.includes('/edit') || currentPath.includes('/new');

      if (isOnEditorPage && editorDirtyState) {
        setPendingNavigation(navigationPayload);
      } else {
        executeNavigation(navigationPayload);
      }
    },
  );

  // useEffectEvent ensures these always read the latest lang, notificationApi, etc.
  const handleConversionSuccess = useEffectEvent(
    (assetId, { result, knowledgeTitle, fileName, options, asset, knowledgeId, categoryId }) => {
      dispatch({
        type: CONVERSION_ACTIONS.UPDATE,
        assetId,
        update: { status: 'DONE', results: result.data },
      });

      closeToast(`conversion-started-${assetId}`);

      createNotification(notificationApi, 'success', {
        message: lang.COMPLETED_TITLE,
        description: lang.COMPLETED_DESCRIPTION.replace('{fileName}', knowledgeTitle || fileName),
        key: `conversion-completed-${assetId}`,
        btn: (
          <div style={{ display: 'flex', gap: '20px' }}>
            <Button
              key="cancel"
              type="danger"
              size="small"
              onClick={() => {
                clearAssetToasts(assetId);
              }}>
              {lang.CANCEL_PUBLISH}
            </Button>
            <Button
              key="goto"
              size="small"
              type="primary"
              onClick={() =>
                navigateToItem({
                  assetId,
                  knowledgeId,
                  result,
                  fileName,
                  categoryId,
                  asset,
                  options,
                })
              }>
              {lang.GO_TO_ITEM}
            </Button>
          </div>
        ),
      });
    },
  );

  const handleConversionError = useEffectEvent(
    (assetId, { error, knowledgeTitle, fileName, options }) => {
      dispatch({
        type: 'UPDATE',
        assetId,
        update: { status: 'FAILED', error: error.message || lang.ERROR },
      });

      closeToast(`conversion-started-${assetId}`);

      createNotification(notificationApi, 'error', {
        message: lang.FAILED_TITLE,
        description: lang.FAILED_DESCRIPTION.replace('{fileName}', knowledgeTitle || fileName),
        key: `conversion-failed-${assetId}`,
        closeIcon: <FontAwesomeIcon icon={faXmark} size="lg" className={styles.closeTagIcon} />,
        onClose: () => clearAssetToasts(assetId),
        btn: options.onRetry && (
          <Button
            size="small"
            type="danger"
            onClick={() => {
              clearAssetToasts(assetId);
              options.onRetry();
            }}>
            {lang.TRY_AGAIN}
          </Button>
        ),
      });
    },
  );

  const startConversion = async (asset, options) => {
    const { assetId } = asset;
    const { knowledgeId, knowledgeTitle, categoryId, fileName } = options;

    // Abort any existing polling for this asset before starting a new one
    abortControllers.current[assetId]?.abort();
    const controller = new AbortController();
    abortControllers.current[assetId] = controller;

    dispatch({
      type: 'UPDATE',
      assetId,
      update: { assetId, knowledgeId, knowledgeTitle, status: 'IN_PROGRESS' },
    });

    // reset active flag when starting new conversions
    isActive.current = true;
    createNotification(notificationApi, 'info', {
      message: lang.STARTED_TITLE,
      description: lang.STARTED_DESCRIPTION.replace('{fileName}', knowledgeTitle || fileName),
      key: `conversion-started-${assetId}`,
      icon: (
        <div className={styles.toastIconWrapper}>
          <FontAwesomeIcon icon={faHourglass} spin className={styles.toastIcon} />
        </div>
      ),
    });

    try {
      const result = await convertToSmartFormat({
        asset,
        knowledgeId,
        knowledgeTitle,
        categoryId,
        fileName,
        signal: controller.signal,
      });

      delete abortControllers.current[assetId];

      if (!isActive.current || controller.signal.aborted) {
        // conversions were cleared (org switch) or this asset was cancelled
        return;
      }

      if (result.status === 'DONE') {
        handleConversionSuccess(assetId, {
          result,
          knowledgeTitle,
          fileName,
          options,
          asset,
          knowledgeId,
          categoryId,
        });
      } else {
        throw new Error('Conversion failed');
      }
    } catch (error) {
      delete abortControllers.current[assetId];
      if (isActive.current && !controller.signal.aborted) {
        handleConversionError(assetId, { error, knowledgeTitle, fileName, options });
      }
    }
  };

  return (
    <ConversionContext.Provider
      value={{
        conversions,
        startConversion,
        getConversionStatus,
        getConversionResult,
        getActiveConversions,
        clearConversions,
        isConvertingKnowledge,
        clearAssetToasts,
        setEditorDirtyState,
      }}>
      {contextHolder}
      <ConfirmationPopupComponent
        open={!!pendingNavigation}
        title={lang.UNSAVED_CHANGES_TITLE}
        message={lang.UNSAVED_CHANGES_MESSAGE}
        onConfirm={handleConfirmNavigation}
        onDismiss={handleCancelNavigation}
        confirmMessage={lang.UNSAVED_CHANGES_CONFIRM}
        dismissMessage={lang.UNSAVED_CHANGES_DISMISS}
        confirmButtonStyle="danger"
      />
      {children}
    </ConversionContext.Provider>
  );
};

export const useConversion = () => {
  const ctx = useContext(ConversionContext);
  if (!ctx) throw new Error('useConversion must be used within a ConversionProvider');
  return ctx;
};
